import { useCallback, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import Api from "../services/Api";

async function changeFlag(flagName, method) {
  const userFlagsResponse = await Api.fetch(
    `/api/User/flags?flagName=${flagName}`,
    {
      method,
    }
  );

  if (!userFlagsResponse.ok) {
    const errorText = await userFlagsResponse.text();
    throw new Error(
      `${userFlagsResponse.status} ${userFlagsResponse.statusText}: ${errorText}`
    );
  }

  return userFlagsResponse.json();
}

function useFlagMutation(flagName, method, onSuccess) {
  return useMutation({
    mutationFn: () => changeFlag(flagName, method),
    onSuccess,
    onError: (err) =>
      console.error(
        `Error ${method === "POST" ? "adding" : "removing"} ${flagName}:`,
        err
      ),
  });
}

export function useSaveFiltersFlag(setUserFlags) {
  const userFlags =
    useSelector((state) => state.user.currentUser?.userFlags) || [];

  const isSaveFiltersEnabled = useMemo(
    () => userFlags.includes("SaveFilters"),
    [userFlags]
  );

  const addMutation = useFlagMutation("SaveFilters", "POST", setUserFlags);
  const removeMutation = useFlagMutation("SaveFilters", "DELETE", setUserFlags);

  const isMutating = useMemo(
    () => addMutation.isLoading || removeMutation.isLoading,
    [addMutation.isLoading, removeMutation.isLoading]
  );

  const error = useMemo(
    () => addMutation.error || removeMutation.error || null,
    [addMutation.error, removeMutation.error]
  );

  const toggleSaveFilters = useCallback(async () => {
    if (isMutating) {
      return;
    }

    isSaveFiltersEnabled
      ? await removeMutation.mutateAsync()
      : await addMutation.mutateAsync();
  }, [isMutating, isSaveFiltersEnabled, addMutation, removeMutation]);

  return {
    isSaveFiltersEnabled,
    toggleSaveFilters,
    isMutating,
    error,
  };
}
